export type CalendarDay = {
  date: string;
  day: number;
  weekday: number;
  inMonth: boolean;
};

function parseDate(date: string): Date {
  return new Date(`${date}T12:00:00.000Z`);
}

export function formatDate(date: Date): string {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

export function addDays(date: string, amount: number): string {
  const next = parseDate(date);
  next.setUTCDate(next.getUTCDate() + amount);
  return formatDate(next);
}

export function startOfWeekMonday(date: string): string {
  const value = parseDate(date);
  const distance = (value.getUTCDay() + 6) % 7;
  value.setUTCDate(value.getUTCDate() - distance);
  return formatDate(value);
}

export function buildMonthGrid(year: number, month: number): CalendarDay[] {
  const first = `${year}-${String(month).padStart(2, "0")}-01`;
  const start = startOfWeekMonday(first);
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(start, index);
    const parsed = parseDate(date);
    return {
      date,
      day: parsed.getUTCDate(),
      weekday: parsed.getUTCDay(),
      inMonth: parsed.getUTCFullYear() === year && parsed.getUTCMonth() + 1 === month,
    };
  });
}

type BulkDateOptions = {
  excludeWeekends?: boolean;
  excludeHolidays?: boolean;
  holidays?: Set<string>;
};

export function weekdayOf(date: string) {
  return parseDate(date).getUTCDay();
}

export function isSunday(date: string) {
  return weekdayOf(date) === 0;
}

export function isWeekend(date: string) {
  const weekday = weekdayOf(date);
  return weekday === 0 || weekday === 6;
}

export function filterBulkDates(
  dates: string[],
  {
    excludeWeekends = true,
    excludeHolidays = true,
    holidays = new Set<string>(),
  }: BulkDateOptions = {},
): string[] {
  return dates.filter((date) => {
    const weekday = weekdayOf(date);
    if (weekday === 0) return false;
    if (excludeWeekends && weekday === 6) return false;
    if (excludeHolidays && holidays.has(date)) return false;
    return true;
  });
}

export function datesBetween(start: string, end: string): string[] {
  const dates: string[] = [];
  for (let date = start; date <= end; date = addDays(date, 1)) dates.push(date);
  return dates;
}
